// Diario de actividad del TPV — cola en memoria + localStorage y envío por lotes.
//
// Coste por evento: construir un objeto pequeño y meterlo en un array. Sin
// Redux ni re-renders. Se envía cada 5 s (o al llegar a 100 eventos) y al
// ocultarse/cerrarse la página con `fetch keepalive`. Si la red falla, el
// lote se queda en la cola (persistida) y se reintenta con backoff: los
// eventos no se pierden por estar offline ni por recargar.

import {
  FLUSH_AT,
  FLUSH_INTERVAL_MS,
  MAX_BATCH,
  MAX_QUEUE,
  backoffMs,
  buildEvent,
  shouldRetry,
  takeKeepaliveBatch,
} from "./journalCore";

export const STORAGE_KEY = "tmo:journal:v1";
export const ENDPOINT = "tpv-journal/batch";

/**
 * deps: {
 *   storage: { getItem, setItem },
 *   send(events) → Promise<{ status }> (lanza error con status en fallo),
 *   sendKeepalive(events) → boolean,
 *   now() → ms, setTimer(fn, ms) → id, clearTimer(id),
 *   isActive() → bool, context() → { actionId, screen, mesaId, sessionId }
 * }
 */
export function createJournalClient(deps) {
  let queue = [];
  let loaded = false;
  let dropped = 0;
  let sending = false;
  let attempt = 0;
  let retryAt = 0;
  let timer = null;
  let persistTimer = null;

  const load = () => {
    if (loaded) return;
    loaded = true;
    try {
      const raw = deps.storage?.getItem(STORAGE_KEY);
      const saved = raw ? JSON.parse(raw) : null;
      if (Array.isArray(saved?.queue)) queue = saved.queue.concat(queue);
      if (saved?.dropped) dropped += saved.dropped | 0;
    } catch { /* storage corrupto: se ignora */ }
  };

  const persistNow = () => {
    persistTimer = null;
    try {
      deps.storage?.setItem(STORAGE_KEY, JSON.stringify({ queue, dropped }));
    } catch { /* cuota llena: la cola sigue en memoria */ }
  };

  const schedulePersist = () => {
    if (persistTimer != null) return;
    persistTimer = deps.setTimer(persistNow, 1000);
  };

  const ensureTimer = () => {
    if (timer != null) return;
    timer = deps.setTimer(tick, FLUSH_INTERVAL_MS);
  };

  function tick() {
    timer = null;
    flush().finally(() => {
      if (queue.length) ensureTimer();
    });
  }

  function enqueue(ev) {
    load();
    queue.push(ev);
    if (queue.length > MAX_QUEUE) {
      const extra = queue.length - MAX_QUEUE;
      queue.splice(0, extra);
      dropped += extra;
    }
    schedulePersist();
    ensureTimer();
    if (queue.length >= FLUSH_AT) flush();
  }

  /** Registra un evento. Devuelve el evento (o null si el diario no aplica aquí). */
  function track(type, data, extra = {}) {
    try {
      if (!deps.isActive()) return null;
      const ev = buildEvent(type, data, extra, { now: deps.now(), ...(deps.context() || {}) });
      enqueue(ev);
      return ev;
    } catch {
      return null; // el diario nunca rompe la operativa del TPV
    }
  }

  async function flush() {
    load();
    if (sending || !queue.length) return;
    if (deps.now() < retryAt) return;
    if (dropped > 0) {
      // Deja constancia de que se descartaron eventos por cola llena.
      queue.unshift(buildEvent("ui.other", { kind: "journalDropped", count: dropped }, {}, { now: deps.now() }));
      dropped = 0;
    }
    const batch = queue.slice(0, MAX_BATCH);
    sending = true;
    try {
      await deps.send(batch);
      queue.splice(0, batch.length);
      attempt = 0;
      retryAt = 0;
      schedulePersist();
    } catch (err) {
      const status = err?.status ?? err?.response?.status ?? null;
      if (shouldRetry(status)) {
        retryAt = deps.now() + backoffMs(attempt);
        attempt += 1;
      } else {
        // 400/413…: lote que el servidor nunca aceptará → se descarta.
        queue.splice(0, batch.length);
        attempt = 0;
        schedulePersist();
      }
    } finally {
      sending = false;
    }
  }

  /** Al ocultarse/cerrarse la página: envío con `keepalive` de lo que quepa. */
  function flushOnHide() {
    load();
    if (!queue.length) return;
    const batch = takeKeepaliveBatch(queue);
    if (batch.length && deps.sendKeepalive && deps.sendKeepalive(batch)) {
      queue.splice(0, batch.length);
    }
    if (persistTimer != null) deps.clearTimer(persistTimer);
    persistNow();
  }

  return {
    track,
    flush,
    flushOnHide,
    // tests / diagnóstico
    _state: () => ({ queue: queue.slice(), dropped, attempt, retryAt, sending }),
  };
}
